import React, { useState, useEffect } from "react";
import axios from "axios";
import Head from "../components/Head";
import Foot from "../components/Foot";
import Quickcontact from "../components/Quickcontact";
import "./Home.css";

const apiUrl = process.env.REACT_APP_API_URL;

const BadgeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2.5l2.47 4.85 5.36.78-3.88 3.78.92 5.34L12 14.77l-4.87 2.48.92-5.34-3.88-3.78 5.36-.78L12 2.5z"
      stroke="#137173"
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={18} height={19} viewBox="0 0 18 19" fill="none">
    <path
      d="M1.7085 9.01147H16.2918H1.7085ZM16.2918 9.01147L9.29183 2.01147L16.2918 9.01147ZM16.2918 9.01147L9.29183 16.0115L16.2918 9.01147Z"
      fill="#030917"
    />
    <path
      d="M1.7085 9.01147H16.2918M16.2918 9.01147L9.29183 2.01147M16.2918 9.01147L9.29183 16.0115"
      stroke="#030917"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CircleArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={10} height={10} viewBox="0 0 10 10" fill="none">
    <path
      d="M0.828613 5H9.16195M9.16195 5L5.16195 1M9.16195 5L5.16195 9"
      stroke="#030917"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill={filled ? "#f5b301" : "none"}>
    <path
      d="M12 2.5l2.7 5.6 6.1.9-4.4 4.4 1 6.2L12 16.6l-5.4 2.9 1-6.2-4.4-4.4 6.1-.9L12 2.5z"
      stroke={filled ? "#f5b301" : "#c7c7c7"}
      strokeWidth={1.3}
      strokeLinejoin="round"
    />
  </svg>
);

const QuoteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={34} height={26} viewBox="0 0 34 26" fill="none">
    <path
      d="M14.5 0L9 12H14V26H0V12.5L6 0H14.5ZM34 0L28.5 12H33.5V26H19.5V12.5L25.5 0H34Z"
      fill="rgba(19, 113, 115, 0.12)"
    />
  </svg>
);

function getInitials(name) {
  if (!name) return "";
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function formatBlogDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Testimonial quote lengths vary wildly (some are several paragraphs). The
// creative-effect swiper stacks slides via CSS transform but still sizes its
// flex wrapper to the tallest slide's natural content height, so an
// unbounded quote blows up the whole section with blank space. Cap it to a
// single tidy excerpt to match the card-carousel format.
function truncate(text, max = 160) {
  if (!text) return "";
  const clean = text.trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max).replace(/\s+\S*$/, "") + "…";
}

function Home() {
  const [banners, setBanners] = useState([]);
  const [badges, setBadges] = useState([]);
  const [content, setContent] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [logos, setLogos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [counters, setCounters] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${apiUrl}/api/public/home-banners`),
      axios.get(`${apiUrl}/api/public/home-badges`),
      axios.get(`${apiUrl}/api/public/home-content`),
      axios.get(`${apiUrl}/api/public/home-highlights`),
      axios.get(`${apiUrl}/api/public/home-featured-courses`),
      axios.get(`${apiUrl}/api/public/accreditations`),
      axios.get(`${apiUrl}/api/public/testimonials`),
      axios.get(`${apiUrl}/api/public/home-counters`),
      axios.get(`${apiUrl}/api/public/blogs/recent?limit=3`),
    ]).then(([b, badg, c, h, fc, l, t, cnt, blogs]) => {
      if (b.status === "fulfilled" && b.value.data.success) setBanners(b.value.data.items);
      if (badg.status === "fulfilled" && badg.value.data.success) setBadges(badg.value.data.items);
      if (c.status === "fulfilled" && c.value.data.success) setContent(c.value.data.content);
      if (h.status === "fulfilled" && h.value.data.success) setHighlights(h.value.data.items);
      if (fc.status === "fulfilled" && fc.value.data.success) setFeaturedCourses(fc.value.data.items);
      if (l.status === "fulfilled" && l.value.data.success) setLogos(l.value.data.accreditations);
      // The "creative" 3D swiper effect was only ever designed/tested against
      // the original ~8 hardcoded testimonials — with all 45+ real ones it
      // miscalculates its layout height, leaving a huge blank gap. Cap it to
      // a curated top N (existing admin ordering) to match original intent.
      if (t.status === "fulfilled" && t.value.data.success) setTestimonials(t.value.data.testimonials.slice(0, 9));
      if (cnt.status === "fulfilled" && cnt.value.data.success) setCounters(cnt.value.data.items);
      if (blogs.status === "fulfilled" && blogs.value.data.success) setRecentBlogs(blogs.value.data.blogs);
      setLoaded(true);
    });
  }, []);

  // The theme's own swiper/counter initialization (assets/js/main.js) runs
  // once on the initial DOMContentLoaded pass, before this data-driven
  // content exists in the DOM — so those sliders/counters need to be
  // (re)initialized here once the real slide markup is actually rendered.
  useEffect(() => {
    if (!loaded) return;
    // Guard against the DOM element itself, not component-instance state —
    // React 18 StrictMode (dev only) and route re-renders can fire this
    // effect against a fresh component instance, so a useRef guard alone
    // isn't reliable here.
    const initOnce = (selector, options) => {
      const el = document.querySelector(selector);
      if (el && !el.classList.contains("swiper-initialized")) {
        return new window.Swiper(selector, options);
      }
      return null;
    };
    const raf = setTimeout(() => {
      if (!window.Swiper) return;

      if (document.querySelector(".ak-slider-hero-2 .swiper-slide")) {
        initOnce(".ak-slider-hero-2", {
          loop: true, speed: 1500, autoplay: false, slidesPerView: 1,
          runCallbacksOnInit: true, parallax: true,
          zoom: { maxRatio: 1.2, minRation: 1 },
          navigation: { nextEl: ".hero-two-next-btn", prevEl: ".hero-two-prev-btn" },
        });
      }
      if (document.querySelector(".ak-slider-service .swiper-slide")) {
        initOnce(".ak-slider-service", {
          loop: true, speed: 500, autoplay: false, spaceBetween: 15, slidesPerView: "auto",
          pagination: { clickable: true },
          navigation: { nextEl: ".service-next-btn", prevEl: ".service-prev-btn" },
        });
      }
      if (document.querySelector(".home-testimonial-slider .swiper-slide")) {
        // autoplay deliberately omitted: combined with loop:true, Swiper 8's
        // autoplay timer can start a transition that collides with a
        // user-initiated slideNext()/slidePrev() shortly after, leaving
        // swiper.animating stuck true forever and permanently disabling the
        // nav arrows. Manual navigation only avoids that failure mode.
        initOnce(".home-testimonial-slider", {
          loop: true, speed: 600, autoplay: false,
          spaceBetween: 24, slidesPerView: 1,
          breakpoints: { 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } },
          navigation: { nextEl: ".testmonial-next-btn", prevEl: ".testmonial-prev-btn" },
        });
      }
      if (document.querySelector(".ak-slider-client-logo .swiper-slide")) {
        initOnce(".ak-slider-client-logo", { loop: true, speed: 1000, autoplay: true, slidesPerView: "auto" });
      }
      if (window.gsap && document.querySelector(".funfact.style1") && !document.querySelector(".funfact.style1").dataset.counted) {
        document.querySelector(".funfact.style1").dataset.counted = "1";
        const counterEls = window.gsap.utils.toArray(".counter");
        counterEls.forEach((num) => {
          window.gsap.from(num, {
            scrollTrigger: { trigger: ".funfact.style1", start: "top center+=200" },
            delay: 0.3, innerText: 0, duration: 3, snap: { innerText: 1 },
          });
        });
      }

      // The theme's title/scroll animations (assets/js/main.js:
      // titleAnimation, textAnimationFadeUp, scrollTransform) run once at
      // load too, before any of this async content exists — re-run them
      // here the same way the theme itself re-runs on repeat calls (it kills
      // any previous animation/split first, so this is safe to call again).
      const { gsap, ScrollTrigger, SplitText } = window;
      if (gsap && SplitText) {
        gsap.utils.toArray(".animation-title").forEach((quote) => {
          if (quote.animation) {
            quote.animation.progress(1).kill();
            quote.split.revert();
          }
          quote.split = new SplitText(quote, { type: "lines,words,chars", linesClass: "split-line" });
          gsap.set(quote, { perspective: 400 });
          const baseDuration = window.innerWidth > 768 ? 1 : 0.8;
          const baseStagger = window.innerWidth > 768 ? 0.02 : 0.04;
          quote.animation = gsap.fromTo(
            quote.split.chars,
            { opacity: 0, x: "50" },
            {
              opacity: 1, x: "0", duration: baseDuration, ease: "back.out(1.7)", stagger: baseStagger,
              scrollTrigger: { trigger: quote, start: "top 80%", end: "top 50%", scrub: false },
            }
          );
        });

        gsap.utils.toArray(".title-anim").forEach((quote) => {
          if (quote.animation) {
            quote.animation.progress(1).kill();
            quote.split.revert();
          }
          quote.split = new SplitText(quote, { type: "lines,words,chars" });
          gsap.set(quote, { overflow: "hidden" });
          quote.animation = gsap.from(quote.split.lines, {
            scrollTrigger: { trigger: quote, start: "top 80%" },
            duration: 0.5, y: 100, autoAlpha: 0,
            stagger: { from: "start", amount: 0.2 },
          });
        });
      }

      if (gsap && document.querySelector(".image-content") && !document.querySelector(".image-content").dataset.parallaxBound) {
        document.querySelector(".image-content").dataset.parallaxBound = "1";
        const images = gsap.utils.toArray(".about-img-1");
        const imagesTwo = gsap.utils.toArray(".about-img-2");
        if (images.length > 0 || imagesTwo.length > 0) {
          const scrollTriggerConfig = {
            trigger: ".image-content", start: "top 80%", end: "bottom 0%", scrub: 1,
            toggleActions: "play reverse play reverse",
          };
          document.addEventListener("wheel", (dets) => {
            const direction = dets.deltaY > 0 ? 1 : -1;
            gsap.to(images, { scrollTrigger: scrollTriggerConfig, y: direction > 0 ? 15 : 0, duration: 1.5, ease: "power1.out" });
            gsap.to(imagesTwo, { scrollTrigger: scrollTriggerConfig, y: direction > 0 ? -15 : 0, x: direction > 0 ? -15 : 0, duration: 1.5, ease: "power1.out" });
          }, { passive: true });
        }
      }

      if (ScrollTrigger) ScrollTrigger.refresh();
    });
    return () => clearTimeout(raf);
  }, [loaded]);

  const c = content || {};

  return (
    <>
      <Head />

      {/* Start Hero */}
      {banners.length > 0 && (
        <section className="ak-slider ak-slider-hero ak-slider-hero-2">
          <div className="swiper-wrapper">
            {banners.map((b) => (
              <div className="swiper-slide" key={b.id}>
                <div className="ak-hero ak-style1 slide-inner" data-swiper-parallax={1500}>
                  {b.image && (
                    <img
                      src={`${apiUrl}/${b.image}`}
                      className="ak-hero-bg h-100 w-100 object-cover"
                      alt="..."
                    />
                  )}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(90deg, rgba(3,9,23,0.55) 0%, rgba(3,9,23,0.35) 45%, rgba(3,9,23,0.05) 75%)",
                      zIndex: 1,
                    }}
                  />
                  <div className="container-md">
                    <div className="hero-slider-info" style={{ zIndex: 2 }}>
                      <div className="slider-info" style={{ maxWidth: 650 }}>
                        <div className="hero-title">
                          {b.mini_title && <p className="mini-title">{b.mini_title}</p>}
                          <h1 className="hero-main-title">{b.title}</h1>
                          {b.description && <p className="main-desp">{b.description}</p>}
                        </div>
                        <div className="ak-height-50 ak-height-lg-40" />
                        {b.button_text && (
                          <div>
                            <a href={b.button_link || "/contacts"} className="common-btn style-2 color-1">
                              <span>{b.button_text}</span>
                              <span className="arrow-cricle">
                                <CircleArrowIcon />
                                <CircleArrowIcon />
                              </span>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="social-medial hero-2">
            <ul className="content">
              <li><a href="https://www.facebook.com/ifmeduacademyindia">FACEBOOK</a></li>
              <li><a href="https://www.linkedin.com/company/ifmeduacademyindia/">LINKEDIN</a></li>
              <li><a href="https://www.youtube.com/@ifmeduacademyindia">YOUTUBE</a></li>
              <li><a href="https://www.instagram.com/ifmeduacademyindia">INSTAGRAM</a></li>
            </ul>
          </div>
          <div className="swiper-controller hero-2">
            <button className="hero-two-next-btn slider-btn next color-2 style-2">
              <svg xmlns="http://www.w3.org/2000/svg" width={26} height={25} viewBox="0 0 26 25" fill="none">
                <path d="M24.1992 12.1344L1.28255 12.1344M1.28255 12.1344L12.2826 1.1344M1.28255 12.1344L12.2826 23.1344" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="hero-two-prev-btn slider-btn prev color-2 style-2">
              <svg xmlns="http://www.w3.org/2000/svg" width={26} height={25} viewBox="0 0 26 25" fill="none">
                <path d="M1.28223 12.1344L24.1989 12.1344M24.1989 12.1344L13.1989 1.1344M24.1989 12.1344L13.1989 23.1344" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </section>
      )}
      {/* End Hero */}

      {/* Start Badges */}
      {badges.length > 0 && (
        <div className="ak-whiteLite-bg home-badges-section">
          <div className="container">
            <div className="home-badges-grid">
              {badges.map((b) => (
                <div className="home-badge-card" key={b.id}>
                  <span className="home-badge-icon">
                    <BadgeIcon />
                  </span>
                  <h5 className="home-badge-title">{b.title}</h5>
                  <a href={b.link_url || "#"} className="more-btn">
                    <span>Learn More</span>
                    <span className="svg-icon">
                      <ArrowIcon />
                      <ArrowIcon />
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* End Badges */}

      {/* Start Moving Section */}
      {c.marquee_text && (
        <>
          <div className="ak-height-60 ak-height-lg-40" />
          <div className="moving-section-wrap home-marquee moving-section-hover-push">
            <div className="moving-section-in">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div className="moving-section home-marquee-item" key={i}>
                  <h2>{c.marquee_text}</h2>
                  <span className="home-marquee-star" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none">
                      <path d="M12 2.5l1.9 6.6 6.6 1.9-6.6 1.9L12 19.5l-1.9-6.6-6.6-1.9 6.6-1.9L12 2.5z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {/* End Moving Section */}

      {/* Start About */}
      {(c.about_title || c.about_body) && (
        <>
          <div className="ak-height-60 ak-height-lg-40" />
          <section className="container">
            <div className="about-us-content">
              <div className="image-content">
                {c.about_image1 && <img className="about-img-1" src={`${apiUrl}/${c.about_image1}`} alt="..." />}
                {c.about_image2 && <img className="about-img-2" src={`${apiUrl}/${c.about_image2}`} alt="..." />}
              </div>
              <div className="info-content">
                <div className="ak-section-heading ak-style-1 animation-title-content animation-style3">
                  {c.about_subtitle && <p className="ak-section-subtitle animation-title">{c.about_subtitle}</p>}
                  {c.about_title && <h2 className="ak-section-title animation-title">{c.about_title}</h2>}
                  {c.about_body && <div className="ak-section-desp" dangerouslySetInnerHTML={{ __html: c.about_body }} />}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      {/* End About */}

      {/* Start Feature */}
      {highlights.length > 0 && (
        <>
          <div className="ak-height-125 ak-height-lg-80" />
          <section className="container">
            <div className="home-highlights-grid">
              {highlights.map((h) => (
                <div className="home-highlight-card" key={h.id}>
                  {h.icon && (
                    <span className="home-highlight-icon">
                      <img src={`${apiUrl}/${h.icon}`} alt="..." />
                    </span>
                  )}
                  <h5 className="home-highlight-title">{h.title}</h5>
                  {h.description && <p className="home-highlight-desp">{h.description}</p>}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      {/* End Feature */}

      {/* Start Service */}
      {featuredCourses.length > 0 && (
        <>
          <div className="ak-height-125 ak-height-lg-80" />
          <section>
            <div className="container">
              <div className="service-top">
                <div className="ak-section-heading ak-style-1 animation-title-content animation-style3 ak-w-50">
                  <p className="ak-section-subtitle animation-title">Courses</p>
                  <h2 className="ak-section-title animation-title">Expert-Led Programs Tailored to Your Goals</h2>
                </div>
                <div className="service-swiper-controller">
                  <button className="service-next-btn slider-btn next">
                    <svg width={10} height={18} viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00928 0.73999L1.00928 8.73999L9.00928 16.74" stroke="#030917" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button className="service-prev-btn slider-btn prev">
                    <svg width={10} height={18} viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.00928 0.73999L9.00928 8.73999L1.00928 16.74" stroke="#030917" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="ak-height-60 ak-height-lg-50" />
            <div className="service-bg">
              <div className="ak-white-bg-service" />
              <div className="container">
                <div className="ak-slider ak-slider-service">
                  <div className="swiper-wrapper">
                    {featuredCourses.map((fc) => (
                      <div className="swiper-slide" key={fc.id}>
                        <div className="service-card h-100">
                          <a href={`/${fc.course_slug}`} className="service-thumb">
                            {fc.image && <img src={`${apiUrl}/${fc.image}`} alt="..." />}
                            <div className="overlay-image">
                              {fc.image && <img src={`${apiUrl}/${fc.image}`} alt="..." />}
                            </div>
                          </a>
                          <div className="service-info">
                            <a href={`/${fc.course_slug}`} className="service-title">{fc.heading}</a>
                          </div>
                          <div className="service-footer">
                            <a href={`/${fc.course_slug}`} className="common-btn style-2 color-2">
                              <span>Enroll</span>
                              <span className="arrow-cricle">
                                <CircleArrowIcon />
                                <CircleArrowIcon />
                              </span>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ak-border-width" />
                <div className="ak-height-100 ak-height-lg-50" />
              </div>
            </div>
          </section>
        </>
      )}
      {/* End Service */}

      {/* Start Testimonial */}
      {(logos.length > 0 || testimonials.length > 0) && (
        <>
          <div className="ak-height-125 ak-height-lg-80" />
          <section>
            {logos.length > 0 && (
              <div className="container">
                <div className="home-logo-heading ak-section-heading ak-style-1 animation-title-content animation-style3">
                  <p className="ak-section-subtitle animation-title">Recognitions</p>
                  <h2 className="ak-section-title animation-title">Accreditations &amp; Certifications</h2>
                </div>
              </div>
            )}
            <div className="ak-height-40 ak-height-lg-30" />
            {logos.length > 0 && (
              <div className="ak-slider ak-slider-client-logo home-logo-slider">
                <div className="swiper-wrapper">
                  {logos.map((l) => (
                    <div className="swiper-slide" key={l.id}>
                      <div className="home-logo-card">
                        <img src={`${apiUrl}/${l.imagepath}`} alt={l.title || "..."} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="ak-border-width" />
            <div className="ak-height-100 ak-height-lg-50" />
            {testimonials.length > 0 && (
              <div className="container">
                <div className="testmonial-top-content">
                  <div className="ak-section-heading ak-style-1 animation-title-content animation-style3">
                    <p className="ak-section-subtitle animation-title">Feedback</p>
                    <h2 className="ak-section-title animation-title">Learning, Evolving, Leading: IFM Student Voices Speak Out</h2>
                  </div>
                </div>
                <div className="ak-height-60 ak-height-lg-50" />
                <div className="home-testimonial-slider ak-slider">
                  <div className="swiper-wrapper">
                    {testimonials.map((t) => (
                      <div className="swiper-slide" key={t.id}>
                        <div className="home-testimonial-card">
                          <QuoteIcon />
                          <div className="home-testimonial-stars">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <StarIcon key={n} filled={n <= (t.rating || 5)} />
                            ))}
                          </div>
                          <p className="home-testimonial-text">{truncate(t.testimonial, 180)}</p>
                          <div className="home-testimonial-person">
                            {t.photo ? (
                              <img className="home-testimonial-avatar" src={`${apiUrl}/${t.photo}`} alt={t.name} />
                            ) : (
                              <span className="home-testimonial-avatar home-testimonial-avatar-fallback">
                                {getInitials(t.name)}
                              </span>
                            )}
                            <div>
                              <p className="home-testimonial-name">{t.name}</p>
                              <p className="home-testimonial-role">{[t.designation, t.company].filter(Boolean).join(", ")}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ak-height-40 ak-height-lg-30" />
                <div className="testmonial-controller home-testimonial-controller">
                  <div className="testmonial-swiper-controller">
                    <button className="testmonial-prev-btn slider-btn prev">
                      <svg width={10} height={18} viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.00928 0.73999L9.00928 8.73999L1.00928 16.74" stroke="#030917" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button className="testmonial-next-btn slider-btn next">
                      <svg width={10} height={18} viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.00928 0.73999L1.00928 8.73999L9.00928 16.74" stroke="#030917" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  <div>
                    <p className="ak-black-color mb-0 ak-medium">Over 100+</p>
                    <p className="ak-font-16 mb-0">Student's Feedback</p>
                  </div>
                </div>
              </div>
            )}
          </section>
        </>
      )}
      {/* End Testimonial */}

      {/* Start Fun Fact Counter */}
      {counters.length > 0 && (
        <>
          <div className="ak-height-135 ak-height-lg-80" />
          <div className="funfact-counter">
            <div className="container">
              <div className="funfact-inner">
                <div className="funfact-title-content ak-section-heading animation-title-content animation-style3">
                  <h3 className="funfact-title ak-section-title">Our Extraordinary Achievement</h3>
                </div>
                <div className="auto-counter-section" id="counter">
                  {counters.map((cn, idx) => (
                    <React.Fragment key={cn.id}>
                      <div className="funfact style1">
                        <div className="funfact-number color-1">
                          <span className="counter">{cn.number}</span>
                          <span>{cn.suffix}</span>
                        </div>
                        <div className="funfact-text">
                          <p className="text">{cn.label}</p>
                        </div>
                      </div>
                      {idx < counters.length - 1 && (
                        <div className="divider-border d-none d-xxl-inline">
                          <svg xmlns="http://www.w3.org/2000/svg" width={38} height={121} viewBox="0 0 38 121" fill="none">
                            <path opacity="0.1" d="M0.502868 120.342L37.2834 0.457802" stroke="#030917" />
                          </svg>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* End Fun Fact Counter */}

      {/* Start Strategic */}
      {(c.strategic_title || c.strategic_panel1_title || c.strategic_panel2_title) && (
        <>
          <div className="ak-height-125 ak-height-lg-80" />
          <section className="container-xxl" id="strategicCard">
            <div className="strategic-card-content">
              <div className="strategic" data-src={c.strategic_image ? `${apiUrl}/${c.strategic_image}` : "assets/img/bg/strategic-bg.png"}>
                <div className="strategic-info">
                  <div className="ak-section-heading ak-style-1">
                    {c.strategic_subtitle && <p className="ak-section-subtitle animation-title">{c.strategic_subtitle}</p>}
                    {c.strategic_title && <h2 className="ak-section-title title-anim">{c.strategic_title}</h2>}
                  </div>
                  <div className="ak-height-50 ak-height-lg-30" />
                  <div className="personalized-long-term">
                    {c.strategic_panel1_title && (
                      <div className="personalized">
                        <img className="icon" src="/assets/img/icon/target.svg" alt="..." />
                        <h5 className="title">{c.strategic_panel1_title}</h5>
                        {c.strategic_panel1_desc && <p className="desp">{c.strategic_panel1_desc}</p>}
                      </div>
                    )}
                    {c.strategic_panel1_title && c.strategic_panel2_title && <div className="personalized-divder" />}
                    {c.strategic_panel2_title && (
                      <div className="long-term">
                        <img className="icon" src="/assets/img/icon/line-chart.svg" alt="..." />
                        <h5 className="title">{c.strategic_panel2_title}</h5>
                        {c.strategic_panel2_desc && <p className="desp">{c.strategic_panel2_desc}</p>}
                      </div>
                    )}
                  </div>
                  {c.strategic_button_text && (
                    <>
                      <div className="ak-height-50 ak-height-lg-30" />
                      <a href={c.strategic_button_link || "/contacts"} className="common-btn">
                        <span>{c.strategic_button_text}</span>
                        <span><CircleArrowIcon /></span>
                      </a>
                    </>
                  )}
                </div>
                <div className="strategic-img">
                  <img src={c.strategic_image ? `${apiUrl}/${c.strategic_image}` : "/assets/img/strategic/sticky-1.png"} alt="..." />
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      {/* End Strategic */}

      {/* Start Blog */}
      {recentBlogs.length > 0 && (
        <>
          <div className="ak-height-50" />
          <section>
            <div className="container">
              <div className="row g-5">
                <div className="col-lg-12 col-xl-4">
                  <div className="ak-section-heading ak-style-1 animation-title-content animation-style3">
                    <p className="ak-section-subtitle animation-title">Recent Blog</p>
                    <h2 className="ak-section-title animation-title">Career Insights and Inspiration</h2>
                    <div className="ak-height-50 ak-height-lg-50" />
                    <a href="/blogs" className="more-btn">
                      <span>View All Blogs</span>
                      <span className="svg-icon">
                        <ArrowIcon />
                        <ArrowIcon />
                      </span>
                    </a>
                  </div>
                </div>
                <div className="col-lg-12 col-xl-8">
                  <div className="blog-content style-1">
                    {recentBlogs.map((blog) => (
                      <a href={`/blogs/${blog.id}`} className="blog-card style-1" key={blog.id}>
                        {blog.featured_image && (
                          <img className="blog-hover-img" src={`${apiUrl}/${blog.featured_image}`} alt="..." />
                        )}
                        <div className="blog-right">
                          <p className="short-title">{formatBlogDate(blog.created_at)}</p>
                          <h5 className="title">{blog.title}</h5>
                        </div>
                        <div className="blog-left">
                          <div className="more-btn">
                            <span>Read More</span>
                            <span className="svg-icon">
                              <ArrowIcon />
                              <ArrowIcon />
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      {/* End Blog */}

      <Quickcontact />
      <Foot />
    </>
  );
}

export default Home;
